import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import { graphql } from '@/gql';
import { FetchProjectSolarPotentialMutation } from '@/gql/graphql';

const FetchProjectSolarPotentialMutationDocument = graphql(`
  mutation FetchProjectSolarPotential(
    $projectId: ID!
    $location: SolarLatLngInput
  ) {
    fetchProjectSolarPotential(projectId: $projectId, location: $location) {
      ...SolarEstimateSummaryFields
    }
  }
`);

/** The user-confirmed building point the analysis should run on. */
export type SolarPotentialLocation = { lat: number; lng: number };

/**
 * Fetches (or reuses the 30-day cache of) the Google Solar API potential for
 * the project's site address. On success the read-side
 * `GetProjectSolarPotential` query is invalidated — `Project.solarPotential`
 * resolves from the row this mutation just persisted, so the refetch never
 * calls out to Google.
 *
 * `location` is the CONFIRMED building point from the confirm-location flow:
 * it persists onto the site address and the analysis runs exactly there (cache
 * bypassed), instead of trusting Google's findClosest snap on the raw geocode —
 * which can cross a party wall and analyse a neighbour's roof.
 */
const useFetchProjectSolarPotential = (
  options?: UseMutationOptions<
    FetchProjectSolarPotentialMutation,
    Error,
    { projectId: string; location?: SolarPotentialLocation }
  >
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: ({ projectId, location }) => {
      return gqlClient.request(FetchProjectSolarPotentialMutationDocument, {
        projectId,
        location,
      });
    },
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.solarPotential(variables.projectId),
      });
      onSuccess?.(data, variables, context);
    },
    meta: { errorMessage: 'Failed to fetch the solar potential' },
    ...restOptions,
  });
};

export default useFetchProjectSolarPotential;
